import { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { useAccount } from "@/context/AccountAuthContext";
import { IMG_PALM_BEACH } from "@/lib/assets";
import { Loader2, CreditCard, Check, PartyPopper, AlertTriangle, ArrowRight, ArrowLeft, Star, Plus, Trash2, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REF = new Date(2026, 6, 6); // season start, for age computation

function computeAge(dob) {
  if (!dob) return null;
  const b = new Date(dob);
  if (isNaN(b.getTime())) return null;
  let age = REF.getFullYear() - b.getFullYear();
  const m = REF.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && REF.getDate() < b.getDate())) age -= 1;
  return age;
}
function groupFor(age, groups) {
  if (age == null) return null;
  return groups.find((g) => age >= g.min && age <= g.max) || null;
}

let seq = 1;

export default function Membership() {
  const { t, lang } = useLang();
  const account = useAccount();
  const m = t.member;
  const [apiAgeGroups, setApiAgeGroups] = useState(null);
  const ageGroups = (apiAgeGroups && apiAgeGroups.length)
    ? apiAgeGroups.map((g) => ({ min: g.min_age, max: g.max_age, name: lang === "fr" ? g.name_fr : (g.name_en || g.name_fr) }))
    : t.booking.ageGroups;
  useEffect(() => { axios.get(`${API}/age-groups`).then((r) => setApiAgeGroups(r.data.age_groups || [])).catch(() => setApiAgeGroups(null)); }, []);

  const [member, setMember] = useState({ name: "", email: "", phone: "", address: "" });
  const [children, setChildren] = useState([{ id: seq++, first_name: "", last_name: "", dob: "", medical: "", interests: "" }]);
  const [emergency, setEmergency] = useState({ name: "", phone: "" });
  const [consents, setConsents] = useState({ image_rights: false, outdoor_activities: false, swimming: false, emergency_transport: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payState, setPayState] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => { axios.get(`${API}/site-settings`).then((r) => setSettings(r.data || {})).catch(() => {}); }, []);
  useEffect(() => {
    if (account?.user) {
      setMember((mm) => ({ ...mm, name: mm.name || account.user.name || "", email: mm.email || account.user.email || "", phone: mm.phone || account.user.phone || "" }));
    }
  }, [account?.user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPayState("processing");
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setPayState("success"); return; }
        if (res.data.status === "expired") { setPayState("failed"); return; }
      } catch (e) { console.warn("Vérification du paiement — nouvelle tentative", e); }
      attempts += 1;
      if (attempts >= 8) { setPayState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const addChild = () => setChildren((c) => [...c, { id: seq++, first_name: "", last_name: "", dob: "", medical: "", interests: "" }]);
  const removeChild = (id) => setChildren((c) => (c.length === 1 ? c : c.filter((x) => x.id !== id)));
  const updateChild = (id, field, value) => setChildren((c) => c.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

  const handlePay = async () => {
    setError("");
    if (!member.name.trim() || !validEmail(member.email)) { setError(m.requiredFields); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setLoading(true);
    try {
      const payloadChildren = children
        .map((c) => ({ first_name: c.first_name.trim(), last_name: c.last_name.trim(), dob: c.dob.trim(), medical: c.medical.trim(), interests: c.interests.trim() }))
        .filter((c) => c.first_name || c.last_name || c.dob || c.medical || c.interests);
      const res = await axios.post(`${API}/membership/checkout`, {
        member: { name: member.name.trim(), email: member.email.trim(), phone: member.phone.trim() },
        address: member.address.trim(),
        children: payloadChildren,
        emergency_name: emergency.name.trim(),
        emergency_phone: emergency.phone.trim(),
        consents,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setLoading(false);
      setError(m.payError);
    }
  };

  if (payState === "processing") {
    return <Shell><Loader2 className="animate-spin text-[#e07a5f]" size={48} /><h1 className="font-head font-bold text-2xl text-[#6f7d54] mt-5">{m.processing}</h1></Shell>;
  }
  if (payState === "success") {
    return (
      <Shell>
        <div className="w-20 h-20 rounded-full bg-[#5e7d52]/15 flex items-center justify-center mb-2"><PartyPopper className="text-[#5e7d52]" size={40} /></div>
        <h1 className="font-head font-extrabold text-3xl text-[#6f7d54]" data-testid="member-success">{m.success}</h1>
        <p className="font-body text-[#3a4d68] mt-3 max-w-md">{m.successText}</p>
        <a href="/reservation" data-testid="member-go-book" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#6f7d54] text-white font-bold px-7 py-3.5">{m.goBook} <ArrowRight size={18} /></a>
      </Shell>
    );
  }
  if (payState === "pending") {
    return (
      <Shell>
        <div className="w-20 h-20 rounded-full bg-[#e0b36a]/20 flex items-center justify-center mb-2"><Loader2 className="animate-spin text-[#8a6a1f]" size={40} /></div>
        <h1 className="font-head font-bold text-2xl text-[#6f7d54]" data-testid="member-pending">{m.paymentPending}</h1>
        <p className="font-body text-[#3a4d68] mt-3 max-w-md">{m.paymentPendingText}</p>
        <a href="/mon-compte" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#6f7d54] text-white font-bold px-7 py-3.5">{m.viewMyAccount}</a>
      </Shell>
    );
  }
  if (payState === "failed") {
    return (
      <Shell>
        <div className="w-20 h-20 rounded-full bg-[#e63946]/12 flex items-center justify-center mb-2"><AlertTriangle className="text-[#e63946]" size={40} /></div>
        <h1 className="font-head font-bold text-2xl text-[#6f7d54]">{m.failed}</h1>
        <a href="/adhesion" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#e07a5f] text-white font-bold px-7 py-3.5"><ArrowLeft size={18} /> {t.nav.member}</a>
      </Shell>
    );
  }

  return (
    <div data-testid="membership-page" className="bg-[#fbf6ec] min-h-screen">
      <div className="relative pt-32 pb-14 bg-[#6f7d54] overflow-hidden">
        <img src={IMG_PALM_BEACH} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#6f7d54] to-[#6f7d54]/60" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 lg:px-10">
          <h1 className="font-head font-extrabold text-white text-3xl md:text-5xl tracking-tight">{m.title}</h1>
          <p className="font-body text-[#f7efe0]/85 mt-3">{m.subtitle}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
        <div className="space-y-8">
          {error && <div data-testid="member-error" className="bg-[#e63946]/10 border border-[#e63946]/30 text-[#b5232f] rounded-2xl px-5 py-4 font-body font-semibold">{error}</div>}

          {/* Responsable */}
          <section className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 md:p-8">
            <h2 className="font-head font-bold text-xl text-[#6f7d54] mb-5">{m.info}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={m.name} testid="member-name" value={member.name} onChange={(v) => setMember({ ...member, name: v })} />
              <Field label={m.email} testid="member-email" type="email" value={member.email} onChange={(v) => setMember({ ...member, email: v })} />
              <Field label={m.phone} testid="member-phone" value={member.phone} onChange={(v) => setMember({ ...member, phone: v })} />
              <Field label={m.address} testid="member-address" value={member.address} onChange={(v) => setMember({ ...member, address: v })} />
            </div>
            <div className="mt-5 pt-5 border-t border-[#6f7d54]/10">
              <p className="font-head font-semibold text-[#6f7d54] mb-3">{m.emergencyTitle}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={m.emergencyName} testid="member-emergency-name" value={emergency.name} onChange={(v) => setEmergency({ ...emergency, name: v })} />
                <Field label={m.emergencyPhone} testid="member-emergency-phone" value={emergency.phone} onChange={(v) => setEmergency({ ...emergency, phone: v })} />
              </div>
            </div>
          </section>

          {/* Enfants */}
          <section className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 md:p-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-head font-bold text-xl text-[#6f7d54]">{m.childrenTitle}</h2>
              <button onClick={addChild} data-testid="member-add-child" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f]/12 text-[#cf6a50] font-bold text-sm px-4 py-2 hover:bg-[#e07a5f]/20 transition-colors"><Plus size={16} /> {m.addChild}</button>
            </div>
            <div className="space-y-5">
              {children.map((c, i) => {
                const age = computeAge(c.dob);
                const grp = groupFor(age, ageGroups);
                return (
                  <div key={c.id} data-testid={`member-child-${i}`} className="bg-[#fbf6ec]/70 rounded-2xl border border-[#6f7d54]/10 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-head font-semibold text-[#6f7d54]">{c.first_name || `${m.childFirstName} ${i + 1}`}</span>
                      {children.length > 1 && <button onClick={() => removeChild(c.id)} data-testid={`member-remove-child-${i}`} className="text-[#3a4d68]/50 hover:text-[#e63946]"><Trash2 size={16} /></button>}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label={m.childFirstName} testid={`member-child-first-${i}`} value={c.first_name} onChange={(v) => updateChild(c.id, "first_name", v)} />
                      <Field label={m.childLastName} testid={`member-child-last-${i}`} value={c.last_name} onChange={(v) => updateChild(c.id, "last_name", v)} />
                      <Field label={m.dob} testid={`member-child-dob-${i}`} type="date" value={c.dob} onChange={(v) => updateChild(c.id, "dob", v)} />
                      <div className="flex flex-col justify-end">
                        <span className="font-body font-semibold text-sm text-[#3a4d68] block mb-1.5">{m.groupHint}</span>
                        <div data-testid={`member-child-group-${i}`} className="rounded-xl px-4 py-3 font-body font-bold text-sm border border-[#2e8b9e]/25 bg-[#2e8b9e]/8 text-[#2e8b9e]">
                          {age == null ? m.groupUnknown : (grp ? `${grp.name} · ${age} ${m.yearsShort}` : `${m.outOfRange} (${age} ${m.yearsShort})`)}
                        </div>
                      </div>
                    </div>
                    <label className="block mt-4">
                      <span className="font-body font-semibold text-sm text-[#3a4d68] block mb-1.5">{m.medical}</span>
                      <textarea data-testid={`member-child-medical-${i}`} value={c.medical} onChange={(e) => updateChild(c.id, "medical", e.target.value)} placeholder={m.medicalPlaceholder} rows={2} className="w-full rounded-xl border border-[#6f7d54]/15 bg-white px-4 py-3 font-body text-[#6f7d54] outline-none focus:border-[#e07a5f] transition-colors resize-none" />
                    </label>
                    <label className="block mt-4">
                      <span className="font-body font-semibold text-sm text-[#3a4d68] block mb-1.5">{m.interests}</span>
                      <input data-testid={`member-child-interests-${i}`} value={c.interests} onChange={(e) => updateChild(c.id, "interests", e.target.value)} placeholder={m.interestsPlaceholder} className="w-full rounded-xl border border-[#6f7d54]/15 bg-white px-4 py-3 font-body text-[#6f7d54] outline-none focus:border-[#e07a5f] transition-colors" />
                    </label>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Consentements */}
          <section data-testid="member-consents" className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-1"><ShieldCheck size={20} className="text-[#5e7d52]" /><h2 className="font-head font-bold text-xl text-[#6f7d54]">{m.consentsTitle}</h2></div>
            <p className="font-body text-sm text-[#3a4d68]/70 mb-5">{m.consentsIntro}</p>
            <div className="space-y-3">
              <ConsentCheck testid="member-consent-image" checked={consents.image_rights} onChange={(v) => setConsents({ ...consents, image_rights: v })} label={m.consentImage} />
              <ConsentCheck testid="member-consent-outdoor" checked={consents.outdoor_activities} onChange={(v) => setConsents({ ...consents, outdoor_activities: v })} label={m.consentOutdoor} />
              <ConsentCheck testid="member-consent-swim" checked={consents.swimming} onChange={(v) => setConsents({ ...consents, swimming: v })} label={m.consentSwim} />
              <ConsentCheck testid="member-consent-transport" checked={consents.emergency_transport} onChange={(v) => setConsents({ ...consents, emergency_transport: v })} label={m.consentTransport} />
            </div>
          </section>

          <section className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 md:p-8">
            <h2 className="font-head font-bold text-xl text-[#6f7d54] mb-5">{m.includesTitle}</h2>
            <ul className="space-y-3">
              {t.membership.includes.map((inc, i) => (
                <li key={i} className="flex items-start gap-3 font-body text-[#3a4d68]">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-[#5e7d52]/15 text-[#5e7d52] flex items-center justify-center shrink-0"><Check size={14} /></span>
                  {inc}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <aside className="lg:sticky lg:top-24">
          <div className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 shadow-lg">
            <div className="flex items-center gap-2 text-[#e0b36a] mb-3"><Star size={18} fill="currentColor" /><span className="font-script text-2xl text-[#e07a5f]">Island Camps</span></div>
            <h2 className="font-head font-bold text-xl text-[#6f7d54] mb-4">{m.summary}</h2>
            <div className="flex justify-between font-body text-[#3a4d68] mb-2"><span>{m.feeMembership}</span><span className="font-bold text-[#6f7d54]">50€</span></div>
            <div className="flex justify-between font-body text-[#3a4d68] mb-2"><span>{m.feeInsurance}</span><span className="font-bold text-[#6f7d54]">25€</span></div>
            <div className="flex justify-between items-end mt-4 mb-5 border-t border-[#6f7d54]/10 pt-4">
              <span className="font-head font-bold text-lg text-[#6f7d54]">{m.total}</span>
              <span data-testid="member-total" className="font-head font-extrabold text-3xl text-[#e07a5f]">75€</span>
            </div>
            <button onClick={handlePay} disabled={loading} data-testid="member-pay-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] disabled:opacity-50 text-white font-bold text-lg px-6 py-4 transition-colors">
              {loading ? <><Loader2 className="animate-spin" size={20} /> {m.paying}</> : <><CreditCard size={20} /> {m.pay}</>}
            </button>
            {settings.rib_iban && (
              <div data-testid="rib-info" className="mt-4 rounded-2xl border border-[#6f7d54]/15 bg-[#fbf6ec]/70 p-4 text-sm text-[#3a4d68]">
                <p className="font-head font-bold text-[#6f7d54] mb-1">Ou par virement bancaire</p>
                {settings.bank_name && <p><span className="font-semibold">Banque :</span> {settings.bank_name}</p>}
                {settings.rib_holder && <p><span className="font-semibold">Titulaire :</span> {settings.rib_holder}</p>}
                <p className="break-all"><span className="font-semibold">IBAN :</span> {settings.rib_iban}</p>
                {settings.rib_bic && <p><span className="font-semibold">BIC :</span> {settings.rib_bic}</p>}
                <p className="mt-1 text-xs text-[#3a4d68]/70">Merci d'indiquer le nom de l'enfant en référence du virement.</p>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function ConsentCheck({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer bg-[#fbf6ec]/70 rounded-2xl border border-[#6f7d54]/10 p-4">
      <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#e07a5f]" />
      <span className="font-body text-sm text-[#6f7d54]">{label}</span>
    </label>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="font-body font-semibold text-sm text-[#3a4d68] block mb-1.5">{label}</span>
      <input type={type} value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-[#6f7d54]/15 bg-white px-4 py-3 font-body text-[#6f7d54] outline-none focus:border-[#e07a5f] transition-colors" />
    </label>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-[#fbf6ec] flex flex-col items-center justify-center text-center px-6 pt-24 pb-16">{children}</div>;
}
